// src/components/Events/NewYear/hooks/useNewYearWS.js
import { useCallback, useEffect, useRef, useState } from "react";
import api from "../../../../api/axios";

export default function useNewYearWS({ enabled, pushMatrixText, showToast, setMessages }) {
    const [wsStatus, setWsStatus] = useState("idle");

    const wsRef = useRef(null);
    const reconnectTimerRef = useRef(null);
    const reconnectAttemptRef = useRef(0);
    const connectRef = useRef(null);

    // ✅ 메시지 중복 방지 (id 없을 때도 막기)
    const seenKeyRef = useRef(new Set());
    const seenOrderRef = useRef([]); // 오래된 키 정리용

    const rememberSeen = (key) => {
        if (!key) return;
        if (seenKeyRef.current.has(key)) return;

        seenKeyRef.current.add(key);
        seenOrderRef.current.push(key);

        // 너무 커지면 정리
        if (seenOrderRef.current.length > 500) {
            const cut = seenOrderRef.current.splice(0, 200);
            for (const k of cut) seenKeyRef.current.delete(k);
        }
    };

    const makeFallbackKey = (data, msgText) => {
        // id가 없을 때도 "같은 메시지" 판단할 기준
        const name = (data.display_name || "").trim();
        const at = (data.created_at || "").trim();
        const text = (msgText || "").trim();
        if (!text) return null;
        return `f:${name}|${at}|${text}`;
    };

    const getWsUrl = useCallback(() => {
        const base = api?.defaults?.baseURL;
        if (base && /^https?:\/\//i.test(base)) {
            const u = new URL(base);
            const wsProto = u.protocol === "https:" ? "wss" : "ws";
            return `${wsProto}://${u.host}/ws/events/newyear/`;
        }
        const proto = window.location.protocol === "https:" ? "wss" : "ws";
        return `${proto}://${window.location.host}/ws/events/newyear/`;
    }, []);

    useEffect(() => {
        if (!enabled) {
            setWsStatus("idle");
            try {
                wsRef.current?.close();
            } catch { }
            wsRef.current = null;
            return;
        }

        let canceled = false;

        const clearReconnectTimer = () => {
            if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }
        };

        const connect = () => {
            if (canceled) return;

            try {
                wsRef.current?.close();
            } catch { }
            wsRef.current = null;

            clearReconnectTimer();
            setWsStatus("connecting");

            const ws = new WebSocket(getWsUrl());
            wsRef.current = ws;

            ws.onopen = () => {
                reconnectAttemptRef.current = 0;
                setWsStatus("open");
            };

            ws.onerror = () => {
                setWsStatus("error");
                try {
                    ws.close();
                } catch { }
            };

            ws.onclose = () => {
                setWsStatus("closed");
                if (canceled) return;

                const attempt = Math.min(reconnectAttemptRef.current + 1, 6);
                reconnectAttemptRef.current = attempt;
                const delay = Math.min(300 * 2 ** (attempt - 1), 8000);

                clearReconnectTimer();
                reconnectTimerRef.current = setTimeout(() => {
                    if (!canceled) connect();
                }, delay);
            };

            ws.onmessage = (ev) => {
                try {
                    const data = JSON.parse(ev.data);

                    // ✅ 매트릭스 텍스트는 서버가 보내준 것만 떨어뜨리는 게 가장 안전
                    if (data.matrix_text) pushMatrixText(data.matrix_text, { source: "ws" });

                    const msgText = (data.text ?? data.content ?? "").trim();

                    if (data.type === "newyear_chat" && msgText) {
                        // 서버가 matrix_text를 안 줬다면 여기서 떨어뜨림
                        if (!data.matrix_text) pushMatrixText(msgText, { source: "ws" });

                        const key =
                            data.id != null
                                ? `id:${data.id}`
                                : makeFallbackKey(data, msgText);

                        // ✅ key 중복이면 스킵
                        if (key && seenKeyRef.current.has(key)) return;
                        rememberSeen(key);

                        setMessages((prev) => {
                            // ✅ 1) 로컬(_local)로 먼저 추가해둔 게 있으면 "교체"해서 2개를 1개로 만들기
                            // 마지막 8개 정도만 확인하면 충분 (성능/정확도 균형)
                            const start = Math.max(0, prev.length - 8);
                            for (let i = prev.length - 1; i >= start; i--) {
                                const m = prev[i];
                                if (m && m._local && (m.content || "").trim() === msgText) {
                                    const copy = prev.slice();
                                    copy[i] = {
                                        id: data.id ?? m.id ?? `ws-${Date.now()}-${Math.random().toString(16).slice(2)}`,
                                        content: msgText,
                                        display_name: data.display_name || m.display_name || "",
                                        created_at: data.created_at || m.created_at || "",
                                        _local: false,
                                    };
                                    return copy;
                                }
                            }

                            // ✅ 2) 로컬이 없으면 그냥 append
                            return [
                                ...prev,
                                {
                                    id: data.id ?? `ws-${Date.now()}-${Math.random().toString(16).slice(2)}`,
                                    content: msgText,
                                    display_name: data.display_name || "",
                                    created_at: data.created_at || "",
                                },
                            ];
                        });
                    }

                    if (data.awarded_leaf) showToast(`+${data.awarded_leaf} Leaf 적립!`);
                    if (data.type === "error" && data.error) showToast(data.error);
                } catch { }
            };
        };

        connectRef.current = connect;
        connect();

        return () => {
            canceled = true;
            clearReconnectTimer();
            try {
                wsRef.current?.close();
            } catch { }
            wsRef.current = null;
            connectRef.current = null;
        };
    }, [enabled, getWsUrl, pushMatrixText, setMessages, showToast]);

    const wsReady = wsStatus === "open" && wsRef.current?.readyState === 1;

    const sendWS = useCallback((payload) => {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== 1) return false;
        ws.send(JSON.stringify(payload));
        return true;
    }, []);

    const forceReconnect = useCallback(() => {
        connectRef.current?.();
    }, []);

    return { wsStatus, wsReady, sendWS, forceReconnect };
}
